import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ChartNoAxesColumnIncreasing,
  Copy,
  ExternalLink,
  FileText,
  Lightbulb,
  MessageCircle,
  Shield,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../card';
import { Button } from '../../../button/button';
import { ImprovementResultsProps } from './types';
import type { PromptImproverSuggestion } from '../../../../../lib/types';

const suggestionIcons = [FileText, Shield, MessageCircle];

function formatSuggestionsForClipboard(results: PromptImproverSuggestion[]) {
  return results
    .map((result, index) => {
      const consider = result.consider.map((line) => `  • ${line}`).join('\n');
      return [
        `Suggestion ${index + 1}: ${result.title}`,
        '',
        'Issue',
        result.issue,
        '',
        'Potential impact',
        result.potential_impact,
        '',
        'Consider',
        consider,
      ].join('\n');
    })
    .join('\n\n—\n\n');
}

export const ImprovementResults = ({ results }: ImprovementResultsProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopySuggestions = useCallback(async () => {
    const text = formatSuggestionsForClipboard(results);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('Failed to copy suggestions to clipboard');
    }
  }, [results]);

  return (
    <section className="space-y-5">
      <div
        className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50/90 p-4 text-sm text-amber-950"
        role="note"
      >
        <AlertTriangle
          className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
          aria-hidden
        />
        <p className="leading-relaxed">
          <span className="font-semibold">Important:</span> These are
          suggestions to consider, not guaranteed solutions. LLMs can make
          mistakes — review carefully and re-test your agent after making any
          changes. Changes that improve one area may affect others.
        </p>
      </div>

      <div className="space-y-4">
        {results.map((result, index) => {
          const Icon = suggestionIcons[index % suggestionIcons.length];
          return (
            <Card
              key={`${index}-${result.title}`}
              className="border-[#00000014] bg-prompt-safecomwhite shadow-sm"
            >
              <CardHeader className="space-y-0 pb-3 pt-5">
                <div className="flex items-start gap-3">
                  <Icon
                    className="mt-0.5 h-5 w-5 shrink-0 text-prompt-safecomabbey"
                    aria-hidden
                  />
                  <CardTitle className="text-left text-base font-semibold leading-snug text-prompt-safecomvulcan">
                    Suggestion {index + 1}: {result.title}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 pb-5 pt-0">
                <div className="space-y-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-prompt-safecomoutrageous-orange">
                    Issue
                  </p>
                  <p className="text-sm leading-relaxed text-prompt-safecomvulcan">
                    {result.issue}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-prompt-safecomoutrageous-orange">
                    Potential impact
                  </p>
                  <p className="text-sm leading-relaxed text-prompt-safecomvulcan">
                    {result.potential_impact}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-prompt-safecomoutrageous-orange">
                    Consider
                  </p>
                  <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-prompt-safecomvulcan">
                    {result.consider.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex gap-3 rounded-lg border border-stone-200 bg-stone-100 p-4 text-sm text-prompt-safecomvulcan">
        <Lightbulb
          className="mt-0.5 h-5 w-5 shrink-0 text-orange-500"
          aria-hidden
        />
        <p className="leading-relaxed">
          <span className="font-semibold">Tip:</span> Prompt optimisation is an
          iterative process. Changes that improve one aspect may affect others.
          Continue testing and refining.
        </p>
      </div>

      <Card className="border-[#00000014] bg-prompt-safecomwhite shadow-sm">
        <CardHeader className="space-y-0 pb-3 pt-5">
          <div className="flex items-start gap-3">
            <ChartNoAxesColumnIncreasing
              className="mt-0.5 h-5 w-5 shrink-0 text-orange-600"
              strokeWidth={1.75}
              aria-hidden
            />
            <CardTitle className="text-left text-base font-semibold text-orange-600">
              Next steps
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pb-5 pt-0">
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-prompt-safecomvulcan">
            <li>Review these suggestions carefully</li>
            <li>Apply changes you agree with to your prompt</li>
            <li>Re-run your evaluation to validate improvements</li>
            <li>Iterate as needed</li>
          </ol>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          className="h-10 flex-1 border-[#00000020] bg-prompt-safecomwhite"
          onClick={handleCopySuggestions}
        >
          <Copy className="h-4 w-4" aria-hidden />
          {copied ? 'Copied!' : 'Copy suggestions'}
        </Button>
        <Button
          variant="default"
          data-onboarding="evaluation-go-to-agent"
          className="hover:bg-prompt-safecomoutrageous-orange/90 h-10 flex-1 bg-prompt-safecomoutrageous-orange"
          asChild
        >
          <Link to="/agent-builder">
            <ExternalLink className="h-4 w-4" aria-hidden />
            Go to Agent Builder
          </Link>
        </Button>
      </div>
    </section>
  );
};
